import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@mlm/ui/lib/cn';
import { Eyebrow } from './primitives';

/**
 * Desktop left rail for a stepped flow (onboarding, later the morning ritual):
 * brand, the numbered steps with a hairline marking the current one, and a note.
 * Earlier steps link back when `hrefFor` is given. Hidden below `lg`.
 */
export function StepRail({
  title,
  steps,
  index,
  hrefFor,
  note,
}: {
  title: string;
  steps: readonly string[];
  index: number;
  hrefFor?: (step: number) => string;
  note?: ReactNode;
}) {
  return (
    <aside className="hidden border-r border-border px-10 pb-12 pt-10 lg:flex lg:flex-col lg:justify-between xl:pl-12">
      <div>
        <p className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
          Miracles Protocol
        </p>
        <nav aria-label={title} className="mt-14">
          <Eyebrow className="mb-4">{title}</Eyebrow>
          <ol>
            {steps.map((label, i) => {
              const current = i === index;
              const row = cn(
                'flex items-baseline gap-4 border-l py-3.5 pl-4',
                current ? 'border-verde' : 'border-border',
              );
              const content = (
                <>
                  <span
                    className={cn(
                      'font-mono text-[0.6875rem]',
                      current ? 'text-ink' : 'text-sage-deep',
                    )}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className={cn('text-[0.9375rem]', current ? 'text-ink' : 'text-sage')}>
                    {label}
                  </span>
                </>
              );
              return (
                <li key={label}>
                  {hrefFor && i < index ? (
                    <Link
                      to={hrefFor(i)}
                      className={cn(row, 'transition-colors hover:[&>span]:text-ink')}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div className={row} aria-current={current ? 'step' : undefined}>
                      {content}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
      {note && <p className="text-[0.8125rem] leading-relaxed text-sage">{note}</p>}
    </aside>
  );
}

/**
 * The step's primary button. Phone: pinned to the bottom of the column at full
 * width. Desktop: a hairline-topped bar with the progress line on the left and
 * the button at its natural width on the right.
 */
export function StepFooter({ progress, children }: { progress: number; children: ReactNode }) {
  return (
    <div className="mt-auto pt-8 lg:flex lg:items-center lg:justify-between lg:gap-8 lg:border-t lg:border-border lg:pt-6 lg:[&>button]:min-h-12 lg:[&>button]:w-auto lg:[&>button]:px-11">
      <div className="hidden h-px w-60 bg-[rgba(16,32,26,0.14)] lg:block" aria-hidden>
        <div
          className="h-px bg-verde transition-[width] duration-300"
          style={{ width: `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%` }}
        />
      </div>
      {children}
    </div>
  );
}
